import React, { FC, useState } from 'react';
import { Input } from 'antd';
import Modal from 'Components/Modal/Modal';
import { Button } from 'Components';
import styles from './ModalForm.module.scss';
import useLogin from '../Hooks/LoginHooks';
import { ReactComponent as EyeOpen } from '../../../../Icons/new-eye-oppened.svg';
import { ReactComponent as EyeCross } from '../../../../Icons/new-eye-crossed.svg';
import { useTranslation } from 'react-i18next';

interface Props {
	visible: boolean;
	onCancel: () => void;
	setShowResetPassword: React.Dispatch<React.SetStateAction<boolean>>;
}

const AdminLoginModal: FC<Props> = ({
	visible,
	onCancel,
	setShowResetPassword
}) => {
	const formik = useLogin();
	const [showPass, setShowPass] = useState(false);
	const { t } = useTranslation();

	const viewPass = () => setShowPass(true);
	const hidePass = () => setShowPass(false);

	const forgotPassword = () => {
		onCancel();
		setShowResetPassword(true);
	};

	const ErrorComponent = (
		<div className={styles.errorMsg}>
			<strong>{t('auth.login.errors.invalidCredentials')}</strong> {t('auth.login.errors.invalidCredentialsMsg')}
		</div>
	);

	return (
		<Modal
			visible={visible}
			centered
			onCancel={onCancel}
			footer={null}
			size="xs"
		>
			<div className={styles.container}>
				<div className={styles.title}>{t('auth.layout.adminTitle')}</div>

				<form onSubmit={formik.handleSubmit}>
					<div className={styles.field}>
						<label>{t('auth.login.inputs.username')}</label>
						<Input
							name="username"
							placeholder={t('auth.login.inputs.usernameLabel')}
							size="large"
							value={formik.values.username}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							onPressEnter={() => formik.submitForm()}
							disabled={formik.isSubmitting}
						/>
						{formik.touched.username && formik.errors.username && (
							<div className={styles.errorMsg}>
								{formik.errors.username}
							</div>
						)}
					</div>

					<div className={styles.field}>
						<label>{t('auth.login.inputs.password')}</label>
						<Input
							name="password"
							type={showPass ? 'text' : 'password'}
							placeholder={t('auth.login.inputs.passwordPlaceHolder')}
							size="large"
							suffix={
								showPass ? (
									<EyeCross onClick={hidePass} />
								) : (
									<EyeOpen onClick={viewPass} />
								)
							}
							value={formik.values.password}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							onPressEnter={() => formik.submitForm()}
							disabled={formik.isSubmitting}
						/>
						{formik.touched.password && formik.errors.password && (
							<div className={styles.errorMsg}>
								{formik.errors.password}
							</div>
						)}
					</div>

					{/* API error */}
					{formik.status && ErrorComponent}

					<Button
						type="primary"
						shape="round"
						className="cta-prominent"
						htmlType="submit"
						style={{ marginTop: formik.status ? '0' : '16px' }}
						loading={formik.isSubmitting}
						disabled={formik.isSubmitting}
					>
						{t('auth.login.inputs.login')}
					</Button>
				</form>

				<div className={styles.forgotPassword} onClick={forgotPassword}>
					{t('auth.login.forgotYourPassword')}
				</div>

				<div className={styles.helperText}>
					{t('auth.layout.adminNote')}
				</div>
			</div>
		</Modal>
	);
};

export default AdminLoginModal;
